import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../engine/types';
import { auth as authApi, storeToken, clearToken } from '../lib/api';
import { useGameStore } from './gameStore';
import { useEightBallStore } from './eightBallStore';
import { useTournamentStore } from './tournamentStore';
import { LOGIN_MAX_ATTEMPTS, LOGIN_LOCKOUT_SECONDS } from '../engine/constants';

interface LoginAttemptTracker {
  count: number;
  lockedUntil: number | null;
}

interface AuthState {
  currentUser: Omit<User, 'pinHash'> | null;
  sessionId: number | null;
  isLoading: boolean;
  error: string | null;
  loginAttempts: Record<number, LoginAttemptTracker>;

  login: (userId: number, pin: string) => Promise<'ok' | 'wrong_pin' | 'locked' | 'must_change'>;
  logout: () => Promise<{ gamesCount: number; totalRevenue: number; durationMinutes: number } | null>;
  changePin: (userId: number, newPin: string) => Promise<void>;
  clearError: () => void;
  loadUsers: () => Promise<Omit<User, 'pinHash'>[]>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      currentUser: null,
      sessionId: null,
      isLoading: false,
      error: null,
      loginAttempts: {},

      login: async (userId, pin) => {
        const { loginAttempts } = get();
        const tracker = loginAttempts[userId] ?? { count: 0, lockedUntil: null };

        if (tracker.lockedUntil && Date.now() < tracker.lockedUntil) return 'locked';

        set({ isLoading: true, error: null });
        try {
          const result = await authApi.login(userId, pin);

          if (result.result === 'must_change') {
            const { [userId]: _removed, ...rest } = get().loginAttempts;
            void _removed;
            storeToken(result.token);
            set({ isLoading: false, loginAttempts: rest, currentUser: result.user, sessionId: result.sessionId });
            return 'must_change';
          }

          // result === 'ok'
          const { [userId]: _removed, ...rest } = get().loginAttempts;
          void _removed;
          storeToken(result.token);
          set({ currentUser: result.user, sessionId: result.sessionId, isLoading: false, loginAttempts: rest });
          return 'ok';
        } catch (e) {
          const msg = String(e);
          const newCount = tracker.count + 1;
          const lockedUntil = newCount >= LOGIN_MAX_ATTEMPTS
            ? Date.now() + LOGIN_LOCKOUT_SECONDS * 1000
            : null;

          if (msg.includes('locked')) {
            set({ isLoading: false, loginAttempts: { ...loginAttempts, [userId]: { count: newCount, lockedUntil } } });
            return 'locked';
          }
          set({ isLoading: false, error: msg, loginAttempts: { ...loginAttempts, [userId]: { count: newCount, lockedUntil } } });
          return 'wrong_pin';
        }
      },

      logout: async () => {
        const resetGameStores = () => {
          useGameStore.getState().reset();
          useEightBallStore.getState().reset();
          useTournamentStore.getState().reset();
        };
        try {
          const { summary } = await authApi.logout();
          clearToken();
          resetGameStores();
          set({ currentUser: null, sessionId: null, error: null });
          return summary;
        } catch {
          clearToken();
          resetGameStores();
          set({ currentUser: null, sessionId: null, error: null });
          return null;
        }
      },

      changePin: async (userId, newPin) => {
        const result = await authApi.changePin(userId, newPin);
        if (result.token && result.sessionId) {
          storeToken(result.token);
          set({ currentUser: result.user, sessionId: result.sessionId });
        } else {
          set((s) => ({
            currentUser: s.currentUser?.id === userId ? result.user : s.currentUser,
          }));
        }
      },

      clearError: () => set({ error: null }),

      loadUsers: () => authApi.getUsers(),
    }),
    {
      name: 'cuetitan_auth',
      partialize: (state) => ({
        currentUser: state.currentUser,
        sessionId: state.sessionId,
      }),
    }
  )
);
